import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { select, Store } from '@ngrx/store';
import { Observable, of, ReplaySubject } from 'rxjs';
import { mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { AccountActions } from '../actions';
import { RedirectionService } from '../services/redirection.service';
import { IsAccountPremiumGuard } from './base/is-account-premium.guard';
import { LoadDomainGuard } from './base/load-domain.guard';
import { LoadRolesAndTeamsGuard } from './base/load-roles-and-teams.guard';
import { LoadServicesGuard } from './base/load-services.guard';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionUpdateGuard {

  constructor(
    private store$: Store<fromRoot.State>,
    private router: Router,
    private loadDomainGuard: LoadDomainGuard,
    private loadServicesGuard: LoadServicesGuard,
    private isAccountPremiumGuard: IsAccountPremiumGuard,
    private loadRolesAndTeamsGuard: LoadRolesAndTeamsGuard,
    private redirectionService: RedirectionService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  canActivate(): Observable<boolean> {
    if (isPlatformServer(this.platformId)) {
      return of(true)
    }
    return this.loadDomainGuard.canActivate().pipe(mergeMap((domainLoaded: boolean) => {
      if (domainLoaded) {
        return this.loadServicesGuard.canActivate().pipe(mergeMap((servicesLoaded: boolean) => {
          if (servicesLoaded) {
            return this.isAccountPremiumGuard.canActivate().pipe(mergeMap((accountIsPremium: boolean) => {
              if (accountIsPremium) {
                return this.loadRolesAndTeamsGuard.canActivate().pipe(mergeMap((rolesAndTeamsLoaded: boolean) => {
                  if (rolesAndTeamsLoaded) {
                    return this.isPaymentRequired().pipe(mergeMap((isPaymentRequired: boolean) => {
                      return of(isPaymentRequired);
                    }));
                  }
                }));
              } else {
                this.redirectionService.redirectToSignin();
                return of(false);
              }
            }));
          }
        }));
      }
    }));
  }

  isPaymentRequired(): Observable<boolean> {
    this.store$.dispatch(AccountActions.loadSubscriptionWithPlan());
    const isPaymentRequired$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      select(fromRoot.getSubscription),
      withLatestFrom(
        this.store$.select(fromRoot.getSelectedLanguage),
        this.store$.select(fromRoot.getUserTeams),
        this.store$.select(fromRoot.getActiveTeam),
      ),
      takeUntil(isPaymentRequired$),
    ).subscribe(([subscription, language, teams, activeTeam]) => {
      if (subscription) {
        switch (subscription.status) {
          case Constant.subscriptionStatus.Active:
            if (subscription.automaticRenew) {
              if (activeTeam) {
                this.router.navigateByUrl('/account/team/' + activeTeam.id + '/transfers/sent');
              } else if (teams?.length > 0) {
                this.router.navigateByUrl('/account/team/' + teams[0].id + '/transfers/sent');
              } else {
                this.redirectionService.redirectToSignupDomain();
              }
              isPaymentRequired$.next(false);
            } else {
              isPaymentRequired$.next(true);
            }
            break;
          case Constant.subscriptionStatus.PaymentRequired:
            isPaymentRequired$.next(true);
            break;
          case Constant.subscriptionStatus.Inactive:
            this.redirectionService.redirectToPricingPage(language);
            isPaymentRequired$.next(false);
            break;
          default:
            if (activeTeam) {
              this.router.navigateByUrl('/account/team/' + activeTeam.id + '/transfers/sent');
            } else if (teams?.length > 0) {
              this.router.navigateByUrl('/account/team/' + teams[0].id + '/transfers/sent');
            } else {
              this.redirectionService.redirectToSignupDomain();
            }
            isPaymentRequired$.next(false);
            break;
        }
      }
    });
    return isPaymentRequired$.asObservable();
  }
}
